/**
 * Cambodia is a single timezone with no DST (Asia/Phnom_Penh, UTC+07:00) —
 * CLAUDE.md §6.5 says explicitly not to build timezone machinery. So: one
 * constant offset, and helpers that only exist because the ABA notification
 * text omits the year.
 */

export const PHNOM_PENH_TZ = 'Asia/Phnom_Penh';
export const PHNOM_PENH_OFFSET_MINUTES = 7 * 60;

/** Wall-clock parts as they would read on a phone in Phnom Penh. */
export interface LocalParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number;
  second: number;
}

export function toPhnomPenhParts(date: Date): LocalParts {
  const shifted = new Date(date.getTime() + PHNOM_PENH_OFFSET_MINUTES * 60_000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
  };
}

/** Build a UTC Date from Phnom Penh wall-clock parts. */
export function fromPhnomPenhParts(parts: LocalParts): Date {
  const utcMillis = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return new Date(utcMillis - PHNOM_PENH_OFFSET_MINUTES * 60_000);
}

/**
 * ABA notifications carry "Aug 15, 10:46 AM" with no year (CLAUDE.md §3
 * payment module). Infer the year from `now`, and handle the December→January
 * boundary: a notification dated Dec 31 that arrives on Jan 1 belongs to last
 * year, and a Jan 1 notification seen on Dec 31 (clock skew) belongs to next.
 *
 * Returns null when no nearby year makes the date real — "Feb 29" outside a
 * leap year means we misread the text, and the caller must treat that as an
 * unparsed notification (which alerts ops) rather than quietly booking a
 * payment against March 1.
 */
export function inferYearForMonthDay(
  month: number,
  day: number,
  now: Date = new Date(),
): number | null {
  const nowParts = toPhnomPenhParts(now);
  const candidates = [nowParts.year - 1, nowParts.year, nowParts.year + 1];
  const nowMillis = fromPhnomPenhParts(nowParts).getTime();

  let best: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const year of candidates) {
    const candidate = fromPhnomPenhParts({ year, month, day, hour: 12, minute: 0, second: 0 });
    // Guard against Feb 29 in a non-leap year rolling over to Mar 1.
    const parts = toPhnomPenhParts(candidate);
    if (parts.month !== month || parts.day !== day) continue;
    const distance = Math.abs(candidate.getTime() - nowMillis);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = year;
    }
  }
  return best;
}

const MONTH_NAMES = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

/** "Aug" / "August" / "aug" -> 8. Returns null for anything unrecognised. */
export function parseMonthName(name: string): number | null {
  const key = name.trim().toLowerCase().slice(0, 3);
  const index = MONTH_NAMES.indexOf(key as (typeof MONTH_NAMES)[number]);
  return index === -1 ? null : index + 1;
}

/** Convert "10:46 AM" / "10:46 PM" to 24-hour parts. */
export function parseTwelveHourClock(
  hour: number,
  minute: number,
  meridiem: string,
): { hour: number; minute: number } | null {
  if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;
  const upper = meridiem.trim().toUpperCase();
  if (upper !== 'AM' && upper !== 'PM') return null;
  const h = hour % 12;
  return { hour: upper === 'PM' ? h + 12 : h, minute };
}

export function formatPhnomPenh(date: Date, locale: 'en' | 'km' = 'en'): string {
  const p = toPhnomPenhParts(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  if (locale === 'km') {
    return `${pad(p.day)}/${pad(p.month)}/${p.year} ${pad(p.hour)}:${pad(p.minute)}`;
  }
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}`;
}

export function startOfPhnomPenhDay(date: Date): Date {
  const p = toPhnomPenhParts(date);
  return fromPhnomPenhParts({ ...p, hour: 0, minute: 0, second: 0 });
}

export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 86_400_000);
}

/**
 * Quiet hours (CLAUDE.md §3 `automation` and `assistant`).
 *
 * Cambodia is a single timezone (§6.5), so this is plain wall-clock
 * arithmetic with no offset machinery. The window normally crosses midnight —
 * 21:00 to 08:00 — so wrapping is the common case, not the edge case.
 *
 * Lives in `shared` rather than in either module because both the automated
 * messages and the Business-chat assistant must obey exactly the same window;
 * a merchant who sets quiet hours once means it everywhere.
 */
export function isWithinQuietHours(
  window: { quietHoursStart: string; quietHoursEnd: string },
  now: Date = new Date(),
): boolean {
  const start = minutesOfDay(window.quietHoursStart);
  const end = minutesOfDay(window.quietHoursEnd);
  // Equal bounds mean "no quiet hours", not "quiet all day" — a merchant who
  // clears both fields wants their messages to go out.
  if (start === null || end === null || start === end) return false;

  const parts = toPhnomPenhParts(now);
  const minutes = parts.hour * 60 + parts.minute;
  return end > start ? minutes >= start && minutes < end : minutes >= start || minutes < end;
}

function minutesOfDay(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
}
